import { call, fork, put, select, takeLatest } from 'redux-saga/effects';
import * as http from 'utils/http';
import config from 'config';
import history from 'modules/history';
import _ from 'lodash';
import moment from 'moment';

import { logChangesGenerator } from 'redux/modules/log/log-changes';

import { SHIPPER_ORDER_UPDATE_END } from 'redux/modules/analytics/events';

import { ShipperOrderTracking } from 'redux/modules/analytics/task-measure';

import {
  setInitialShipperOrder,
  setMainLoading,
  setReset,
  setShipperAddresses,
  setShipperUsers,
  setSubmiting,
} from 'redux/modules/shipper-order-form/duck';

import { createShipperOrderLog, openCancellationLog } from 'redux/modules/log/effects';

import { getinitialShipperOrder } from 'redux/modules/shipper-order-form/selectors';

import {
  setCancellationQuote,
  setOpenedBreakModal,
  setOpenedCancelModal,
} from 'redux/modules/shipper-order-edit/duck';

import { fetchAddresses } from 'redux/modules/addresses/effects';

import {
  adaptShipperOrder,
  parseStandstills,
  setUserSettings,
} from 'redux/modules/shipper-order-form/utils';

import { openShipperOrderDetail } from 'redux/modules/shipper-orders/effects';
import { openJourneyDetail } from 'redux/modules/journeys/effects';

import { displayError } from 'redux/modules/error-handler/effects';
import { SHIPPERORDER_UPDATE, HANDLING_NOT_ALLOWED } from 'redux/modules/error-handler/constants';
import { isHandling400Error } from 'redux/modules/error-handler/utils';

import {
  ASSIGNED,
  baseExtra,
  CANCELLATION_EXTRA,
  DISTRIBUTION,
  EXTRA_PERCENT,
  IN_TRANSIT,
  MIXED_MULTI_ROUTE,
  PENDING_ASSIGNMENT,
  PENDING_MODERATION,
  SINGLE_SHIPMENT,
} from 'modules/shipper-order/Form/constants';

import { getCancellationQuote } from './selectors';

const initForm$ = 'shipper-order-edit/initForm$';
const submitEditForm$ = 'shipper-order-edit/submitEditForm$';
const closeModal$ = 'shipper-order-edit/closeModal$';
const closeBreakModal$ = 'shipper-order-edit/closeBreakModal$';
const applyAndKeep$ = 'shipper-order-edit/applyAndKeep$';
const applyAndRelease$ = 'shipper-order-edit/applyAndRelease$';
const toggleOpenModal$ = 'shipper-order-edit/toggleOpenModal$';
const shipperOrderCancel$ = 'shipper-order-form/shipperOrderCancel$';
const shipperOrderCancelQuote$ = 'shipper-order-form/shipperOrderCancelQuote$';

export const initForm = (shipperOrderId) => ({
  type: initForm$,
  payload: {
    shipperOrderId,
  },
});

export const submitForm = (shipperOrder) => ({
  type: submitEditForm$,
  payload: {
    shipperOrder,
  },
});

export const closeModal = (action) => ({
  type: closeModal$,
  payload: {
    action,
  },
});

export const closeBreakModal = () => ({
  type: closeBreakModal$,
  payload: {},
});

export const applyAndKeep = (shipperOrder) => ({
  type: applyAndKeep$,
  payload: {
    shipperOrder,
  },
});

export const applyAndRelease = (shipperOrder) => ({
  type: applyAndRelease$,
  payload: {
    shipperOrder,
  },
});

export const toggleOpenModal = (payload) => ({
  type: toggleOpenModal$,
  payload: payload,
});

export const shipperOrderCancel = (shipperOrder, { price, cost }) => ({
  type: shipperOrderCancel$,
  payload: {
    shipperOrder,
    price,
    cost,
  },
});

export const cancellationQuote = (shipperOrder, { price, cost }) => ({
  type: shipperOrderCancelQuote$,
  payload: {
    shipperOrder,
    price,
    cost,
  },
});

const computeJourney = ({ journeys = [] }) => {
  const journey = (journeys || [])[0];
  return {
    journey: journey
      ? {
          ...journey,
          isGroupage: journey.journey_type === MIXED_MULTI_ROUTE,
        }
      : journey,
  };
};

const computeStandstills = ({ stops, standstills }) => {
  if (!standstills) {
    return [];
  }

  return stops.map((_, stopId) => {
    const standstill = standstills.find(({ stop_id }) => stop_id === stopId);
    if (!standstill) {
      return null;
    }

    const { time_from, time_to, ...rest } = standstill;
    const momentFrom = moment(time_from);
    const momentTo = moment(time_to);
    return {
      time_from: momentFrom.format('HH:mm'),
      time_to: momentTo.format('HH:mm'),
      ...rest,
    };
  });
};

const computeShipperOrder = ({ shipper_order_type, ...shipperOrder }) => ({
  ...shipperOrder,
  ...computeJourney(shipperOrder),
  shipper_order_type: shipper_order_type === SINGLE_SHIPMENT ? DISTRIBUTION : shipper_order_type,
  standstills: computeStandstills(shipperOrder),
  shipments: [
    ...shipperOrder.shipments.map((shipment) => ({
      ...shipment,
      has_dropoff_appointment:
        shipment.has_dropoff_appointment || !!shipment.dropoff_appointment?.time_from,
    })),
  ],
});

function* handleEditFormInit({ payload }) {
  const { shipperOrderId } = payload;

  if (!shipperOrderId) {
    return;
  }

  try {
    yield put(setMainLoading(true));

    const shipperOrder = yield call(
      http.get,
      `${config.API_URL}mercury/shipper_order_edits/${shipperOrderId}/`
    );

    yield put(setInitialShipperOrder(computeShipperOrder(shipperOrder)));

    const { shipper, user } = shipperOrder;

    const shipperUsers = shipper.users;
    yield put(setShipperUsers(shipperUsers));

    const addresses = yield call(fetchAddresses, shipper);
    yield put(setShipperAddresses(addresses));

    yield call(setUserSettings, user);
  } catch (err) {
    yield call(http.getError, err);
  } finally {
    yield put(setMainLoading(false));
  }
}

const getExtra = (target, type, concept) => {
  if (!target) {
    return {};
  }
  return target[type]?.find((extra) => extra.concept === concept) || {};
};

const hasExtraChange = ({ initialShipperOrder, newShipperOrder }, concept) => {
  const initialPrice = getExtra(initialShipperOrder, 'extra_prices', concept);
  const price = getExtra(newShipperOrder, 'extra_prices', concept);

  const priceChange = String(initialPrice.amount) !== String(price.amount);

  return priceChange;
};

const getChanges = (action, initialShipperOrder, newShipperOrder) => {
  return logChangesGenerator.get(action, {
    net_full_price_before: _.get(initialShipperOrder, 'net_full_price'),
    net_full_price_after: _.get(newShipperOrder, 'net_full_price'),
    currency_before: _.get(initialShipperOrder, 'currency'),
    currency_after: _.get(newShipperOrder, 'currency'),
  });
};

export function* handleLog({ newShipperOrder }) {
  const initialShipperOrder = yield select(getinitialShipperOrder);

  if (hasExtraChange({ initialShipperOrder, newShipperOrder }, 'STANDSTILL')) {
    const action = 'UPDATE_PRICE_BY_STANDSTILL';
    yield put(
      createShipperOrderLog({
        action,
        shipperOrderId: newShipperOrder.id,
        changes: getChanges(action, initialShipperOrder, newShipperOrder),
      })
    );
  }

  if (hasExtraChange({ initialShipperOrder, newShipperOrder }, 'MANAGEMENT_FEE')) {
    const action = 'UPDATE_PRICE_BY_INCIDENT';
    yield put(
      createShipperOrderLog({
        action,
        shipperOrderId: newShipperOrder.id,
        changes: getChanges(action, initialShipperOrder, newShipperOrder),
      })
    );
  }

  if (
    hasExtraChange({ initialShipperOrder, newShipperOrder }, 'PRICE_INCREMENT') ||
    hasExtraChange({ initialShipperOrder, newShipperOrder }, 'DISCOUNT')
  ) {
    const action = 'UPDATE_PRICE';

    yield put(
      createShipperOrderLog({
        action,
        shipperOrderId: newShipperOrder.id,
        editableAfterCreation: true,
        changes: getChanges(action, initialShipperOrder, newShipperOrder),
      })
    );
  }

  if (initialShipperOrder?.manual_billing !== newShipperOrder.manual_billing) {
    yield put(
      createShipperOrderLog({
        action: 'ACTION_MANUAL_BILLING',
        shipperOrderId: newShipperOrder.id,
      })
    );
  }
}

function* redirectToModal({ journey, shipperOrder }) {
  if (!journey || shipperOrder.status === PENDING_MODERATION) {
    yield put(openShipperOrderDetail({ id: shipperOrder.id }));
    return;
  }

  yield put(openJourneyDetail({ id: journey.id }));
}

export function* saveShipperOrder(shipperOrder) {
  const adaptedShipperOrder = yield call(adaptShipperOrder, shipperOrder);
  yield put(setSubmiting(true));
  try {
    const newShipperOrder = computeShipperOrder(
      yield call(http.patch, shipperOrder.url, adaptedShipperOrder)
    );

    const { journey } = newShipperOrder;

    yield fork(handleLog, { newShipperOrder });
    yield call(ShipperOrderTracking.end, shipperOrder.id, SHIPPER_ORDER_UPDATE_END, {
      actionType: 'edition',
    });

    const urlParams = new URLSearchParams(window.location.search);
    const redirect = urlParams.get('redirect');

    if (redirect) {
      yield call(history.push, redirect);
      return;
    }

    yield call(redirectToModal, { journey, shipperOrder: newShipperOrder });
  } catch (err) {
    const errorDetails = yield call(http.getError, err);
    if (isHandling400Error(errorDetails)) {
      yield put(displayError(err, HANDLING_NOT_ALLOWED));
    } else {
      yield put(displayError(err, SHIPPERORDER_UPDATE));
    }
  } finally {
    yield put(setSubmiting(false));
  }
}

function* releaseShipperOrder(shipperOrder) {
  try {
    yield call(http.put, shipperOrder.release);
  } catch (err) {
    yield call(http.getError, err);
  }
}

function* saveShipperOrderAndKeep({ payload }) {
  const { shipperOrder } = payload;
  yield put(setOpenedBreakModal(false));
  yield call(saveShipperOrder, shipperOrder);
}

function* saveShipperOrderAndRelease({ payload }) {
  const { shipperOrder } = payload;
  yield put(setOpenedBreakModal(false));
  try {
    yield call(saveShipperOrder, shipperOrder);
    yield call(releaseShipperOrder, shipperOrder);
  } catch (err) {
    yield call(http.getError, err);
  }
}

const pendingAssignmentFlow = function* (shipperOrder) {
  yield call(saveShipperOrder, shipperOrder);
};

const assignedFlow = function* (shipperOrder) {
  const { journey = {} } = shipperOrder;

  if (!journey.isGroupage) {
    yield call(saveShipperOrder, shipperOrder);
    return;
  }

  yield put(setOpenedBreakModal(ASSIGNED));
};

const savingFlow = {
  [PENDING_ASSIGNMENT]: pendingAssignmentFlow,
  [ASSIGNED]: assignedFlow,
  [IN_TRANSIT]: assignedFlow,
  NOT_FOUND: function* (shipperOrder) {
    yield call(saveShipperOrder, shipperOrder);
  },
};

const parseForPricingQuoting = ({
  shipper,
  user,
  shipments,
  extra_prices,
  stops,
  standstills,
  ...shipperOrder
}) => ({
  ...shipperOrder,
  shipper_id: (shipper || {}).id,
  user_id: (user || {}).id,
  shipments: shipments.map((shipment) => ({
    ...shipment,
    id: undefined,
  })),
  extra_prices,
  stops,
  standstills: parseStandstills(stops, standstills),
});

const parseForCostingQuoting = ({ shipments, ...journey }) => ({
  ...journey,
  stops: shipments.map(({ id }) => ({
    shipments: { [id]: '' },
  })),
});

const filterExtra =
  (extra, referral) =>
  ({ concept, referral_shipper_order_id }) => {
    return (
      concept !== extra &&
      (!referral_shipper_order_id ||
        (referral_shipper_order_id && referral === referral_shipper_order_id))
    );
  };

const cancellationExtra = (extras, amount, referral) => {
  return extras.filter(filterExtra(CANCELLATION_EXTRA, referral)).concat(
    baseExtra({
      concept: CANCELLATION_EXTRA,
      type: EXTRA_PERCENT,
      amount: `-${amount}`,
      referral_shipper_order_id: referral,
    })
  );
};

function* handleEditSubmit({ payload }) {
  const { shipperOrder } = payload;
  try {
    const { status } = shipperOrder;

    const flow = savingFlow[status] ? savingFlow[status] : savingFlow.NOT_FOUND;

    yield call(flow, shipperOrder);
  } catch (err) {
    yield call(http.getError, err);
  }
}

function* handleCloseModal({ payload }) {
  const initialShipperOrder = yield select(getinitialShipperOrder);

  if (!initialShipperOrder) {
    return;
  }

  yield call(ShipperOrderTracking.end, initialShipperOrder.id, SHIPPER_ORDER_UPDATE_END, {
    actionType: 'close',
  });

  yield put(setReset());
  yield call(history.goBack);
}

function* handleModalBreak() {
  yield put(setOpenedBreakModal(false));
}

function* handleModalCancel({ payload }) {
  yield put(setOpenedCancelModal(payload));
}

function* handleShipperOrderCancellation({ payload }) {
  const { shipperOrder, price } = payload;
  const { extra_prices = [] } = shipperOrder;

  try {
    const { price: priceQuote } = yield select(getCancellationQuote);

    yield put(
      setCancellationQuote({
        loading: true,
        ready: false,
        price: null,
        cost: null,
      })
    );

    const { id: referral_shipper_order_id } = shipperOrder;
    const { net_full_price: quoteNetFullPrice, extra_prices: quoteExtraPrices } = priceQuote;

    const cancellationExtraPrice = quoteExtraPrices.find(
      (extra) => extra.concept === CANCELLATION_EXTRA
    );

    const extra_costs = [
      {
        ...cancellationExtraPrice,
        amount: quoteNetFullPrice,
        referral_shipper_order_id,
        perc_amount: cancellationExtraPrice.amount,
      },
    ];

    const request = {
      extra_prices: cancellationExtra(extra_prices, price, shipperOrder.id).filter(
        ({ concept }) => concept === CANCELLATION_EXTRA
      ),
      extra_costs,
    };

    const newShipperOrder = yield call(http.post, shipperOrder.cancel, request) || shipperOrder;

    const { journeys = [] } = newShipperOrder;
    const journey = journeys[0] || {};

    yield call(history.goBack);
    yield call(redirectToModal, { journey, shipperOrder: newShipperOrder });

    yield call(ShipperOrderTracking.end, newShipperOrder.id, SHIPPER_ORDER_UPDATE_END, {
      actionType: 'cancellation',
    });

    yield put(
      openCancellationLog({
        action: 'CANCEL_SHIPPER_ORDER',
        shipperOrderId: shipperOrder.id,
      })
    );
  } catch (err) {
    yield call(http.getError, err);
  } finally {
    yield put(
      setCancellationQuote({
        loading: false,
        ready: false,
        price: null,
        cost: null,
      })
    );
  }
}

function* handleCancellationQuote({ payload }) {
  const pricingURL = `${config.API_URL}pricing_quote/`;
  const costingURL = `${config.API_URL}costing_quote/`;

  const { shipperOrder, price } = payload;

  yield put(
    setCancellationQuote({
      loading: true,
      ready: false,
      price: null,
      cost: null,
    })
  );

  const { id: shipperOrderId, extra_prices = [], journeys = [], shipments = [] } = shipperOrder;
  const journey = journeys[0] || {};
  const { extra_costs = [] } = journey;

  const requestExtraPrices = cancellationExtra(extra_prices, price, shipperOrder.id);

  try {
    const priceQuote = yield call(
      http.post,
      pricingURL,
      parseForPricingQuoting({
        ...shipperOrder,
        extra_prices: requestExtraPrices,
      })
    );

    const { net_full_price: quoteNetFullPrice, extra_prices: quoteExtraPrices } = priceQuote;

    const cancellationExtraPrice = quoteExtraPrices.find(
      (extra) => extra.concept === CANCELLATION_EXTRA
    );

    const quoteExtraCosts = [
      ...extra_costs,
      {
        ...cancellationExtraPrice,
        amount: quoteNetFullPrice,
        referral_shipper_order_id: shipperOrderId,
      },
    ];

    const costQuote = yield call(
      http.post,
      costingURL,
      parseForCostingQuoting({
        ...journey,
        shipments,
        extra_prices: requestExtraPrices,
        extra_costs: quoteExtraCosts,
      })
    );

    yield put(
      setCancellationQuote({
        loading: false,
        ready: true,
        price: priceQuote,
        cost: costQuote,
      })
    );
  } catch (err) {
    yield put(
      setCancellationQuote({
        loading: false,
        ready: false,
        price: null,
        cost: null,
      })
    );
    yield call(http.getError, err);
  }
}

export function* watchShipperOrderEdit() {
  yield takeLatest(initForm$, handleEditFormInit);
  yield takeLatest(submitEditForm$, handleEditSubmit);
  yield takeLatest(closeModal$, handleCloseModal);
  yield takeLatest(closeBreakModal$, handleModalBreak);

  yield takeLatest(applyAndKeep$, saveShipperOrderAndKeep);
  yield takeLatest(applyAndRelease$, saveShipperOrderAndRelease);
  yield takeLatest(toggleOpenModal$, handleModalCancel);
  yield takeLatest(shipperOrderCancel$, handleShipperOrderCancellation);

  yield takeLatest(shipperOrderCancelQuote$, handleCancellationQuote);
}

export { handleEditFormInit };
